// Ubicación en el repo: src/components/PatientsTrash.jsx
// "Papelera" de pacientes — sub-pestaña de Configuración (DECISIONS.md
// #75). Pedido de Nicolás (20-ago-2026): antes de esto, una vez eliminada
// una ficha no había ninguna forma en la app de volver a verla ni de
// restaurarla. Solo Admin llega acá — RLS (`patients_select`, 0003) ya
// filtra pacientes eliminados para cualquier otro rol, así que esta
// pantalla igual se gatea del lado del cliente para no mostrar una tabla
// vacía sin explicación.
import { useCallback, useEffect, useState } from 'react';
import { listDeletedPatients, restorePatient, permanentlyDeletePatient, patientHasHistory } from '../lib/patients';
import { formatPatientName, formatDateTime } from '../lib/format';
import Spinner from './Spinner';
import ForceDeletePatientModal from './ForceDeletePatientModal';

export default function PatientsTrash({ isAdmin }) {
  const [patients, setPatients] = useState([]);
  const [historyByPatient, setHistoryByPatient] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listDeletedPatients();
      setPatients(list);
      // patient_has_history() por cada uno — decide si "Eliminar
      // definitivamente" está disponible o no. La papelera normalmente
      // tiene pocas fichas a la vez, así que esto no debería pesar.
      const entries = await Promise.all(list.map(async (p) => [p.id, await patientHasHistory(p.id)]));
      setHistoryByPatient(Object.fromEntries(entries));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la papelera de pacientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  async function handleRestore(p) {
    const nombre = formatPatientName(p);
    const ok = window.confirm(
      `¿Restaurar la ficha de ${nombre}? Sus citas, exámenes, recetas, audífonos y ventas anteriores NO se restauran junto con la ficha — si necesitas alguno de vuelta, hay que hacerlo a mano.`
    );
    if (!ok) return;
    setBusyId(p.id);
    setError('');
    try {
      await restorePatient(p.id);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo restaurar la ficha.');
    } finally {
      setBusyId(null);
    }
  }

  async function handlePermanentDelete(p) {
    const nombre = formatPatientName(p);
    const ok = window.confirm(
      `¿Eliminar DEFINITIVAMENTE la ficha de ${nombre}? Esto no se puede deshacer — la ficha desaparece por completo, no queda en la papelera.`
    );
    if (!ok) return;
    setBusyId(p.id);
    setError('');
    try {
      await permanentlyDeletePatient(p.id);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la ficha definitivamente.');
    } finally {
      setBusyId(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="patients-page">
        <h1>Papelera</h1>
        <p className="text-muted">Solo un Admin puede ver y gestionar la papelera de pacientes.</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Papelera de pacientes</h1>
      </div>

      <p className="field-hint" style={{ marginBottom: 16 }}>
        Fichas eliminadas desde Pacientes. Al eliminar una ficha, sus citas, exámenes, recetas, audífonos y ventas
        quedan eliminados junto con ella — <strong>restaurar la ficha no los trae de vuelta</strong>, solo la ficha
        misma. "Eliminar definitivamente" solo está disponible si la ficha nunca tuvo historial clínico (fichas de
        prueba o duplicadas vacías); si tiene historial, se queda acá para siempre y solo se puede restaurar.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>RUT</th>
            <th>Eliminado</th>
            <th>Eliminado por</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5}><Spinner inline /></td>
            </tr>
          ) : patients.length === 0 ? (
            <tr>
              <td colSpan={5}>La papelera está vacía.</td>
            </tr>
          ) : (
            patients.map((p) => {
              const hasHistory = historyByPatient[p.id];
              const busy = busyId === p.id;
              return (
                <tr key={p.id}>
                  <td>{formatPatientName(p)}</td>
                  <td>{p.rut || '—'}</td>
                  <td>{formatDateTime(p.deleted_at)}</td>
                  <td>{p.professionals ? `${p.professionals.nombre} ${p.professionals.apellido}` : '—'}</td>
                  <td className="actions-cell">
                    <button type="button" className="link-button" disabled={busy} onClick={() => handleRestore(p)}>
                      Restaurar
                    </button>
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      disabled={busy || hasHistory === undefined || hasHistory === true}
                      title={hasHistory ? 'Tiene historial clínico — no se puede eliminar definitivamente.' : ''}
                      onClick={() => handlePermanentDelete(p)}
                    >
                      Eliminar definitivamente
                    </button>
                    {hasHistory === true && (
                      <button
                        type="button"
                        className="link-button link-button--danger"
                        disabled={busy}
                        title="Solo Admin — pasa por encima de la protección de historial clínico, requiere contraseña y motivo."
                        onClick={() => setForceDeleteTarget(p)}
                      >
                        Forzar eliminación (Admin)
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {forceDeleteTarget && (
        <ForceDeletePatientModal
          patient={forceDeleteTarget}
          onClose={() => setForceDeleteTarget(null)}
          onDeleted={() => {
            setForceDeleteTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
